// LocationContext for managing user location state across the application
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import locationService from '../services/locationService';
import { usePlanFeatures } from './PlanFeaturesContext';
import { resolveExpectedStateCode, isLocationInExpectedState } from '../utils/locationStateValidation';

const LocationContext = createContext();

// sessionStorage key for the resolved user location — mirrors the exact pattern
// SearchStateContext already uses for query/filters/plan/pagination (see
// SEARCH_STATE_KEY there). Before this, `userLocation` was the ONLY piece of
// "day-to-day" search-related state in the app that did NOT survive a refresh:
// everything else (search term, filters, selected plan, page) is restored via
// SearchStateContext's sessionStorage mirror, but a resolved location was pure
// in-memory `useState(null)` — wiped on every reload. That mismatch was the root
// cause of two related, user-reported symptoms: (1) refreshing the results page
// re-ran geolocation/reverse-geocoding from scratch instead of reusing the
// already-resolved location, sometimes producing a visibly different address for
// the exact same coordinates; (2) navigating back to the search page afterward
// could show no location at all, since context had been reset (or, prior to the
// results.jsx fix alongside this one, actively clobbered back down to bare
// coordinates). Persisting `userLocation` here, the same way SearchStateContext
// already persists everything else, fixes both for free and removes the need for
// pages to re-derive location from scratch after a refresh.
const LOCATION_STORAGE_KEY = 'userLocation';

function readStoredLocation() {
  try {
    const raw = sessionStorage.getItem(LOCATION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    // sessionStorage can throw in locked-down browser contexts (private/sandboxed
    // iframes) or contain corrupted JSON — never let that crash the app, just fall
    // back to no cached location (matches SearchStateContext's own defensive parsing).
    console.warn('⚠️ [LocationContext] sessionStorage unavailable or corrupted, starting with no cached location:', e);
    return null;
  }
}

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {


  const { updateEnvironmentId, environmentId, planCode, planFeatures } = usePlanFeatures();
  
  // Track the current environment ID to detect actual plan changes
  const currentEnvironmentIdRef = useRef(null);
  
  // Location state — lazy-initialized from sessionStorage (see readStoredLocation above)
  // so a page refresh restores the last-resolved location instead of starting from null.
  const [userLocation, setUserLocation] = useState(readStoredLocation);
  const [detectedPlan, setDetectedPlan] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [detectionMethod, setDetectionMethod] = useState('unknown');
  const [permissionStatus, setPermissionStatus] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'

  // Persist to sessionStorage on every change so a refresh (or navigating back to a page
  // that reads this context, e.g. the search page's "do we already have a location?" check)
  // restores the last-resolved location instead of finding nothing. Mirrors the exact
  // pattern SearchStateContext uses for its own state. Explicitly removes the key when
  // location is cleared (rather than persisting "null") so a stale entry can't leak into
  // a later session.
  useEffect(() => {
    try {
      if (userLocation && userLocation.latitude && userLocation.longitude) {
        sessionStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(userLocation));
      } else {
        sessionStorage.removeItem(LOCATION_STORAGE_KEY);
      }
    } catch (e) {
      console.warn('⚠️ [LocationContext] Failed to persist location to sessionStorage:', e);
    }
  }, [userLocation]);

  // Monitor environment ID changes and clear location when it changes
  // This tracks the actual environment/state, not URL parameter variations
  useEffect(() => {
    // Only proceed if we have a valid environmentId
    if (!environmentId) return;
    
    // If we have a previous environmentId and it's different from current, clear location
    if (currentEnvironmentIdRef.current && currentEnvironmentIdRef.current !== environmentId) {
      console.log('🔄 Environment changed from', currentEnvironmentIdRef.current, 'to', environmentId, '- clearing location');
      setUserLocation(null);
      setDetectedPlan(null);
      setLocationError('');
      setDetectionMethod('unknown');
    }
    
    // Update the ref to track current environmentId
    currentEnvironmentIdRef.current = environmentId;
  }, [environmentId]);

  // Re-validate the current (possibly sessionStorage-restored) location against the plan's
  // expected state whenever we have enough info to check — planCode arrives synchronously
  // on mount (from the URL's :planCode path segment) before planFeatures' async fetch
  // resolves, so this can catch a bad cached location almost immediately rather than only
  // reacting to a later environmentId CHANGE like the effect above. This is a genuinely
  // different, independent safety net from that one: the effect above only fires when
  // environmentId actually changes value (a real plan switch within the same browser
  // session/tab); it does nothing for a location that was read back out of sessionStorage
  // on a FRESH mount already tagged with the CURRENT plan's planCode — e.g. exactly the
  // reported case where a CA address had somehow been resolved and persisted while
  // `planCode: "PA"` was already the active plan, so the existing planCode-mismatch guards
  // elsewhere (search.jsx's/results.jsx's `currentStateLocation` checks) never caught it,
  // since planCode itself matched fine. This re-checks the actual geography, not just which
  // plan session produced it.
  useEffect(() => {
    if (!userLocation?.stateCode) return; // nothing to validate yet (bare coords, no address)

    const expectedState = resolveExpectedStateCode(planCode, planFeatures);
    if (!isLocationInExpectedState(userLocation, expectedState)) {
      console.warn('⚠️ [LocationContext] Cached location is outside the current plan\'s state — clearing it:', {
        locationState: userLocation.stateCode,
        expectedState
      });
      setUserLocation(null);
      setDetectedPlan(null);
      setDetectionMethod('unknown');
    }
    // Only re-run when the plan identity itself changes (or a brand-new location is set) —
    // not on every planFeatures object identity change, which could otherwise re-fire this
    // on unrelated planFeatures updates that don't affect the expected state at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation, planCode, planFeatures?.state, planFeatures?.stateCode]);

  /**
   * Request location permission and detect location
   */
  const requestLocationPermission = useCallback(async () => {

    
    if (!navigator.geolocation) {
      setPermissionStatus('denied');
      setLocationError('Geolocation is not supported by this browser');
      return false;
    }

    try {
      // Check if permissions API is supported
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });

        
        setPermissionStatus(permission.state);
        
        // Listen for permission changes
        permission.addEventListener('change', () => {

          setPermissionStatus(permission.state);
        });

        if (permission.state === 'denied') {
          setLocationError('Location access denied');
          return false;
        }
      }

      return true;
    } catch (error) {

      return true; // Continue anyway, we'll handle it in the location request
    }
  }, []);

  /**
   * Detect user location and associated plan
   */
  const detectLocation = useCallback(async (force = false) => {
    if (locationLoading && !force) {

      return;
    }


    
    setLocationLoading(true);
    setLocationError('');

    try {
      const result = await locationService.detectLocationAndPlan();
      


      setUserLocation(result.location);
      setDetectedPlan(result.plan);
      setDetectionMethod(result.detectionMethod);

      // Update the environment context if we got a valid plan
      if (result.plan && result.plan.environmentId) {

        await updateEnvironmentId(result.plan.environmentId);
      }

      // Clear any previous errors
      setLocationError('');

      return result;

    } catch (error) {


      
      // Location detection failed — surface the error visibly instead of using fake coordinates
      const unknownLocation = { source: 'unknown', formattedAddress: 'Unknown location' };
      setUserLocation(unknownLocation);
      setDetectedPlan(null);
      setDetectionMethod('unknown');
      setLocationError('Unable to detect your location. Please enter it manually.');

      return {
        location: unknownLocation,
        plan: null,
        detectionMethod: 'unknown'
      };

    } finally {
      setLocationLoading(false);
    }
  }, [locationLoading, updateEnvironmentId]);

  /**
   * Manual location update by user
   */
  const updateUserLocation = useCallback(async (locationData) => {


    setLocationLoading(true);
    
    try {
      // REMOVED: Plan lookup API call (not currently needed)
      // const planData = await locationService.getLocationBasedPlan(locationData);
      
      setUserLocation(locationData);
      // setDetectedPlan(planData);
      setDetectionMethod('manual');

      // Update environment
      // if (planData && planData.environmentId) {
      //   await updateEnvironmentId(planData.environmentId);
      // }



    } catch (error) {

      setLocationError(error.message);
    } finally {
      setLocationLoading(false);
    }
  }, [updateEnvironmentId]);

  /**
   * Request permission and detect location on startup
   */
  const initializeLocation = useCallback(async () => {

    
    const hasPermission = await requestLocationPermission();
    
    if (hasPermission) {
      await detectLocation();
    } else {

      await detectLocation(); // This will use IP/fallback methods
    }
  }, [requestLocationPermission, detectLocation]);

  // REMOVED: Auto-detection on mount to prevent conflicts with search page location handling
  // The search page now handles location detection directly
  // useEffect(() => {
  //   initializeLocation();
  // }, [initializeLocation]);

  /**
   * Clear location data
   */
  const clearLocation = useCallback(() => {

    
    setUserLocation(null);
    setDetectedPlan(null);
    setLocationError('');
    setDetectionMethod('unknown');
    setPermissionStatus('unknown');
    
    locationService.clearCache();
  }, []);

  /**
   * Get formatted location string for display
   */
  const getFormattedLocation = useCallback(() => {
    if (!userLocation) return 'Location not available';
    
    if (userLocation.formattedAddress) {
      return userLocation.formattedAddress;
    }
    
    const parts = [
      userLocation.city,
      userLocation.stateCode || userLocation.state,
      userLocation.zipCode
    ].filter(Boolean);
    
    return parts.join(', ') || 'Unknown location';
  }, [userLocation]);

  /**
   * Check if user is in a supported service area
   */
  const isInServiceArea = useCallback(() => {
    if (!detectedPlan || !userLocation) return null;
    
    // Check if the detected plan has valid services
    return detectedPlan.environmentId && detectedPlan.state;
  }, [detectedPlan, userLocation]);

  const contextValue = {
    // State
    userLocation,
    detectedPlan,
    locationLoading,
    locationError,
    detectionMethod,
    permissionStatus,

    // Actions
    detectLocation,
    updateUserLocation,
    clearLocation,
    requestLocationPermission,
    initializeLocation,

    // Getters
    getFormattedLocation,
    isInServiceArea,

    // Direct service access
    locationService
  };

  // Add debug functions to window for development
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.locationDebug = {
        getState: () => ({
          userLocation,
          detectedPlan,
          locationLoading,
          locationError,
          detectionMethod,
          permissionStatus
        }),
        detectLocation: () => detectLocation(true),
        clearLocation,
        getFormattedLocation
      };
      

    }
  }, [userLocation, detectedPlan, locationLoading, locationError, detectionMethod, permissionStatus, detectLocation, clearLocation, getFormattedLocation]);

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
